import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { 
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Input, Badge, Modal, ModalFooter, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
  Tabs, TabsList, TabsTrigger, TabsContent, Select, Textarea
} from '../../components/ui';
import { formatDate, formatRelativeTime } from '../../lib/utils';
import { 
  Users, Shield, RefreshCw, Eye, DollarSign, Clock, Zap,
  Search, Save, CheckCircle, XCircle, Megaphone, Bell, Send, 
  Trash2, ToggleLeft, ToggleRight, Download, ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import OnboardingAdmin from './OnboardingAdmin';
import DemoLeads from './DemoLeads';
import IntegrationSettings from './IntegrationSettings';

export const SystemAdmin = () => {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    type: 'info',
    show_modal: true,
    target_tiers: ['trial', 'starter', 'pro'],
  });

  // Broadcast Notifications state
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    message: '',
    target_tiers: ['trial', 'starter', 'pro']
  });
  const [recentNotifications, setRecentNotifications] = useState([]);

  // Stats
  const [stats, setStats] = useState({ 
    totalUsers: 0, 
    pendingAffiliates: 0, 
    newContacts: 0, 
    proUsers: 0,
    trialUsers: 0,
    starterUsers: 0
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load users
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      setUsers(usersData || []);

      // Load affiliates
      const { data: affiliatesData } = await supabase
        .from('affiliates')
        .select('*')
        .order('created_at', { ascending: false });
      setAffiliates(affiliatesData || []);

      // Load contact submissions
      const { data: contactsData } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      setContacts(contactsData || []);

      // Load announcements
      const { data: announcementsData } = await supabase
        .from('admin_announcements')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      setAnnouncements(announcementsData || []);

      // Load recent notifications
      const { data: notifsData } = await supabase
        .from('user_notifications')
        .select('title, message, type, created_at')
        .eq('type', 'admin_message')
        .order('created_at', { ascending: false })
        .limit(50);
      
      // Deduplicate by title+message
      const uniqueNotifs = [];
      const seen = new Set();
      (notifsData || []).forEach(n => {
        const key = `${n.title}|${n.message}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueNotifs.push(n);
        }
      });
      setRecentNotifications(uniqueNotifs.slice(0, 10));

      // Calculate stats
      const pendingAffiliates = (affiliatesData || []).filter(a => a.status === 'pending').length;
      const newContacts = (contactsData || []).filter(c => c.status === 'new').length;
      const proUsers = (usersData || []).filter(u => u.tier === 'pro').length;
      const starterUsers = (usersData || []).filter(u => u.tier === 'starter').length;
      const trialUsers = (usersData || []).filter(u => u.tier === 'trial').length;

      setStats({
        totalUsers: usersData?.length || 0,
        pendingAffiliates,
        newContacts,
        proUsers,
        trialUsers,
        starterUsers
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateUserProfile = async (userId, updates) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (error) throw error;
      toast.success('User updated');
      loadData();
      setShowUserModal(false);
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const grantTrial = async (userId, days) => {
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + days);
    await updateUserProfile(userId, {
      tier: 'trial',
      trial_ends_at: trialEnd.toISOString(),
    });
  };

  const approveAffiliate = async (id) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('affiliates')
        .update({
          status:      'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq('id', id);
      if (error) throw error;
      toast.success('Affiliate approved');
      setShowAffiliateModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to approve: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const rejectAffiliate = async (id, reason) => {
    if (!reason?.trim()) { toast.error('Please enter a rejection reason'); return; }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('affiliates')
        .update({
          status:           'rejected',
          rejection_reason: reason.trim(),
          reviewed_at:      new Date().toISOString(),
          reviewed_by:      user.id,
        })
        .eq('id', id);
      if (error) throw error;
      toast.success('Affiliate rejected');
      setShowAffiliateModal(false);
      setRejectReason('');
      loadData();
    } catch (error) {
      toast.error('Failed to reject: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Announcement functions
  const createAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('admin_announcements').insert({
        ...newAnnouncement,
        created_by: profile?.id,
        is_active: true
      });

      if (error) throw error;

      toast.success('Announcement created!');
      setNewAnnouncement({
        title: '',
        content: '',
        type: 'info',
        show_modal: true,
        target_tiers: ['trial', 'starter', 'pro'],
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create announcement: ' + error.message);
    }
    setSaving(false);
  };

  const toggleAnnouncement = async (id, currentActive) => {
    try {
      const { error } = await supabase
        .from('admin_announcements')
        .update({ is_active: !currentActive })
        .eq('id', id);

      if (error) throw error;
      toast.success(currentActive ? 'Announcement disabled' : 'Announcement enabled');
      loadData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;

    try {
      const { error } = await supabase
        .from('admin_announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Announcement deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Broadcast notification functions
  const sendBroadcastNotification = async () => {
    if (!broadcastForm.title || !broadcastForm.message) {
      toast.error('Title and message are required');
      return;
    }

    setSaving(true);
    try {
      // Get users matching target tiers
      const { data: targetUsers, error: usersError } = await supabase
        .from('profiles')
        .select('id')
        .in('tier', broadcastForm.target_tiers);

      if (usersError) throw usersError;

      if (!targetUsers || targetUsers.length === 0) {
        toast.error('No users found matching selected tiers');
        setSaving(false);
        return;
      }

      // Create notifications for all matching users
      const notifications = targetUsers.map(u => ({
        user_id: u.id,
        type: 'admin_message',
        title: broadcastForm.title,
        message: broadcastForm.message,
        read: false
      }));

      const { error } = await supabase
        .from('user_notifications')
        .insert(notifications);

      if (error) throw error;

      toast.success(`Notification sent to ${targetUsers.length} users!`);
      setBroadcastForm({
        title: '',
        message: '',
        target_tiers: ['trial', 'starter', 'pro']
      });
      loadData();
    } catch (error) {
      toast.error('Failed to send: ' + error.message);
    }
    setSaving(false);
  };

  const toggleTier = (tier, form, setForm) => {
    const current = form.target_tiers || [];
    if (current.includes(tier)) {
      setForm({ ...form, target_tiers: current.filter(t => t !== tier) });
    } else {
      setForm({ ...form, target_tiers: [...current, tier] });
    }
  };

  const ROLE_LABELS = { solo: 'Solo', team: 'Small team', agency: 'Agency', startup: 'Startup' };
  const EXP_LABELS  = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' };

  const exportUsersCSV = () => {
    const headers = ['ID','Username','Email','Tier','Role','Status','Joined','Use Cases','Onboarding Role','Platforms','Experience'];
    const lines = users.map(u => [
      u.id,
      `"${(u.username || '').replace(/"/g, '""')}"`,
      `"${(u.email || '').replace(/"/g, '""')}"`,
      u.tier || '', u.role || '', u.status || '',
      u.created_at ? new Date(u.created_at).toLocaleDateString() : '',
      `"${(u.onboarding_use_cases || []).join('; ')}"`,
      ROLE_LABELS[u.onboarding_role] || u.onboarding_role || '',
      `"${(u.onboarding_platforms || []).join('; ')}"`,
      EXP_LABELS[u.onboarding_experience] || u.onboarding_experience || '',
    ].join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `revhapi-users-${new Date().toISOString().split('T')[0]}.csv`,
    });
    a.click();
  };

  const filteredUsers = users.filter(u =>
    u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingAffiliates = affiliates.filter(a => a.status === 'pending');
  const newContacts = contacts.filter(c => c.status === 'new');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System Admin</h1>
          <p className="text-muted-foreground">Manage users, affiliates, and messaging</p>
        </div>
        <Button onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-primary" />
              <div>
                <p className="text-xl font-bold">{stats.totalUsers}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-6 w-6 rounded bg-yellow-500 flex items-center justify-center text-white text-xs font-bold">T</div>
              <div>
                <p className="text-xl font-bold">{stats.trialUsers}</p>
                <p className="text-xs text-muted-foreground">Trial</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-6 w-6 rounded bg-blue-500 flex items-center justify-center text-white text-xs font-bold">S</div>
              <div>
                <p className="text-xl font-bold">{stats.starterUsers}</p>
                <p className="text-xs text-muted-foreground">Starter</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-6 w-6 rounded bg-green-500 flex items-center justify-center text-white text-xs font-bold">P</div>
              <div>
                <p className="text-xl font-bold">{stats.proUsers}</p>
                <p className="text-xs text-muted-foreground">Pro</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-warning" />
              <div>
                <p className="text-xl font-bold">{stats.pendingAffiliates}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-1">
        <TabsList className="flex w-max min-w-full gap-0.5">
          <TabsTrigger value="users" className="whitespace-nowrap text-xs sm:text-sm">Users</TabsTrigger>
          <TabsTrigger value="affiliates" className="whitespace-nowrap text-xs sm:text-sm">
            Affiliates {pendingAffiliates.length > 0 && <Badge variant="warning" className="ml-1">{pendingAffiliates.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="contacts" className="whitespace-nowrap text-xs sm:text-sm">
            Messages {newContacts.length > 0 && <Badge variant="warning" className="ml-1">{newContacts.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="announcements" className="whitespace-nowrap text-xs sm:text-sm">
            <Megaphone className="h-3.5 w-3.5 mr-1 hidden sm:block" />
            App Msgs
          </TabsTrigger>
          <TabsTrigger value="plugin-banners" className="whitespace-nowrap text-xs sm:text-sm">
            🛡️ Plugin
          </TabsTrigger>
          <TabsTrigger value="notifications" className="whitespace-nowrap text-xs sm:text-sm">
            <Bell className="h-3.5 w-3.5 mr-1 hidden sm:block" />
            Broadcast
          </TabsTrigger>
          <TabsTrigger value="onboarding" className="whitespace-nowrap text-xs sm:text-sm">
            <Users className="h-3.5 w-3.5 mr-1 hidden sm:block" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="integrations" className="whitespace-nowrap text-xs sm:text-sm">
            Integrations
          </TabsTrigger>
          <TabsTrigger value="demo-leads" className="whitespace-nowrap text-xs sm:text-sm">
            <Zap className="h-3.5 w-3.5 mr-1 hidden sm:block" />
            Demo Leads
          </TabsTrigger>
        </TabsList>
        </div>

        {/* USERS TAB */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle>User Management</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button variant="outline" onClick={exportUsersCSV}>
                    <Download className="h-4 w-4 mr-2" /> Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Onboarding</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableEmpty colSpan={5} message="No users found" />
                  ) : (
                    filteredUsers.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                              {u.username?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="font-medium">{u.username || 'No name'}</p>
                              <p className="text-xs text-muted-foreground">{u.id.substring(0, 8)}...</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.role === 'superadmin' ? 'default' : u.role === 'admin' ? 'secondary' : 'outline'}>
                            {u.role || 'user'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.tier === 'pro' ? 'success' : u.tier === 'starter' ? 'default' : 'warning'}>
                            {u.tier || 'trial'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatRelativeTime(u.created_at)}
                        </TableCell>
                        <TableCell>
                          {u.onboarding_role
                            ? <span className="text-xs text-muted-foreground">{ROLE_LABELS[u.onboarding_role] || u.onboarding_role}{u.onboarding_experience ? ` · ${EXP_LABELS[u.onboarding_experience] || u.onboarding_experience}` : ''}</span>
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => { setSelectedUser(u); setShowUserModal(true); }}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AFFILIATES TAB */}
        <TabsContent value="affiliates">
          <Card>
            <CardHeader>
              <CardTitle>Affiliate Applications</CardTitle>
              <CardDescription>
                {affiliates.filter(a => a.status === 'pending').length} pending ·{' '}
                {affiliates.filter(a => a.status === 'approved').length} approved ·{' '}
                {affiliates.filter(a => a.status === 'rejected').length} rejected
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Channels</TableHead>
                    <TableHead>Applied</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.length === 0 ? (
                    <TableEmpty colSpan={6} message="No affiliate applications yet" />
                  ) : affiliates.map(a => {
                    const applicant = users.find(u => u.id === a.user_id);
                    return (
                      <TableRow key={a.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{applicant?.username || a.user_id?.substring(0, 8)}</p>
                            {a.website_url && (
                              <a href={a.website_url} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center gap-1">
                                <ExternalLink className="h-3 w-3" />
                                {a.website_url.replace(/^https?:\/\//, '').substring(0, 30)}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{a.audience_size || '—'}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[160px]">
                            {(a.marketing_channels || []).slice(0, 3).map(ch => (
                              <Badge key={ch} variant="secondary" className="text-xs capitalize">{ch}</Badge>
                            ))}
                            {(a.marketing_channels || []).length > 3 && (
                              <Badge variant="secondary" className="text-xs">+{a.marketing_channels.length - 3}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(a.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={a.status === 'approved' ? 'success' : a.status === 'pending' ? 'warning' : 'secondary'}>
                            {a.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline"
                            onClick={() => { setSelectedAffiliate(a); setRejectReason(''); setShowAffiliateModal(true); }}>
                            <Eye className="h-4 w-4 mr-1" />Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Affiliate review modal */}
          {showAffiliateModal && selectedAffiliate && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="p-5 border-b border-border flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Affiliate Application</h3>
                    <p className="text-sm text-muted-foreground">
                      {users.find(u => u.id === selectedAffiliate.user_id)?.username || 'Unknown'}
                      {' · Applied '}{formatDate(selectedAffiliate.created_at)}
                    </p>
                  </div>
                  <button onClick={() => setShowAffiliateModal(false)}
                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground">
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-5 space-y-5">
                  {/* Links */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedAffiliate.website_url && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Website</p>
                        <a href={selectedAffiliate.website_url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />{selectedAffiliate.website_url}
                        </a>
                      </div>
                    )}
                    {selectedAffiliate.social_media_url && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Social</p>
                        <a href={selectedAffiliate.social_media_url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />{selectedAffiliate.social_media_url}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Audience */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                      Audience ({selectedAffiliate.audience_size || 'not specified'})
                    </p>
                    <div className="bg-muted/40 border border-border rounded-lg p-3 text-sm">
                      {selectedAffiliate.audience_desc || '—'}
                    </div>
                  </div>

                  {/* Channels */}
                  {(selectedAffiliate.marketing_channels || []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Marketing Channels</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedAffiliate.marketing_channels.map(ch => (
                          <Badge key={ch} variant="outline" className="capitalize">{ch}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Marketing plan */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Marketing Plan</p>
                    <div className="bg-muted/40 border border-border rounded-lg p-3 text-sm whitespace-pre-wrap">
                      {selectedAffiliate.marketing_plan || '—'}
                    </div>
                  </div>

                  {/* Payment */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Payment Method</p>
                      <p className="text-sm capitalize">{(selectedAffiliate.payment_method || '—').replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Payment Details</p>
                      <p className="text-sm font-mono break-all">{selectedAffiliate.payment_details || '—'}</p>
                    </div>
                  </div>

                  {/* Rejection reason if already rejected */}
                  {selectedAffiliate.status === 'rejected' && selectedAffiliate.rejection_reason && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <p className="text-xs font-semibold text-red-500 mb-1">Rejection Reason</p>
                      <p className="text-sm">{selectedAffiliate.rejection_reason}</p>
                    </div>
                  )}

                  {/* Actions for pending */}
                  {selectedAffiliate.status === 'pending' && (
                    <div className="border-t border-border pt-4 space-y-3">
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => approveAffiliate(selectedAffiliate.id)} disabled={saving}>
                        <CheckCircle className="h-4 w-4 mr-2" />Approve Application
                      </Button>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Rejection reason <span className="text-red-500">*</span></label>
                        <textarea
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                          placeholder="Explain why the application is not approved (the applicant will see this)…"
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground h-20 resize-none"
                        />
                        <Button variant="outline" className="w-full text-red-500 border-red-500/30 hover:bg-red-500/10"
                          onClick={() => rejectAffiliate(selectedAffiliate.id, rejectReason)}
                          disabled={saving || !rejectReason.trim()}>
                          <XCircle className="h-4 w-4 mr-2" />Reject Application
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Already decided */}
                  {selectedAffiliate.status !== 'pending' && (
                    <div className="border-t border-border pt-4 flex items-center gap-2 text-sm text-muted-foreground">
                      Reviewed {selectedAffiliate.reviewed_at ? formatDate(selectedAffiliate.reviewed_at) : '—'}
                      <Badge variant={selectedAffiliate.status === 'approved' ? 'success' : 'secondary'} className="ml-1">
                        {selectedAffiliate.status}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* CONTACTS TAB */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <CardTitle>Contact Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableEmpty colSpan={5} message="No messages" />
                  ) : (
                    contacts.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <TableCell className="max-w-xs truncate">{c.subject}</TableCell>
                        <TableCell>
                          <Badge variant={c.status === 'new' ? 'warning' : 'secondary'}>{c.status}</Badge>
                        </TableCell>
                        <TableCell>{formatRelativeTime(c.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ANNOUNCEMENTS TAB */}
        <TabsContent value="announcements">
          <div className="space-y-6">
            <Alert>
              <Megaphone className="h-4 w-4" />
              <AlertDescription>
                <strong>Announcements</strong> appear as a modal popup once per session when users log in. 
                Users can dismiss them and won't see them again until their next session.
              </AlertDescription>
            </Alert>

            {/* Create New Announcement */}
            <Card>
              <CardHeader>
                <CardTitle>Create Announcement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label="Title"
                  placeholder="e.g., New Feature: Currency Converter"
                  value={newAnnouncement.title}
                  onChange={e => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                />
                
                <div>
                  <label className="block text-sm font-medium mb-1">Content (HTML supported)</label>
                  <Textarea
                    placeholder="<p>We've added a new feature!</p>"
                    value={newAnnouncement.content}
                    onChange={e => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <div className="flex gap-2">
                    {['info', 'success', 'warning', 'urgent'].map(type => (
                      <button
                        key={type}
                        onClick={() => setNewAnnouncement({ ...newAnnouncement, type })}
                        className={`px-3 py-1 rounded text-sm capitalize ${
                          newAnnouncement.type === type
                            ? type === 'info' ? 'bg-blue-500 text-white'
                            : type === 'success' ? 'bg-green-500 text-white'
                            : type === 'warning' ? 'bg-yellow-500 text-white'
                            : 'bg-red-500 text-white'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Target Tiers</label>
                  <div className="flex gap-2">
                    {['trial', 'starter', 'pro'].map(tier => (
                      <button
                        key={tier}
                        onClick={() => toggleTier(tier, newAnnouncement, setNewAnnouncement)}
                        className={`px-3 py-1 rounded text-sm capitalize ${
                          (newAnnouncement.target_tiers || []).includes(tier)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="show_modal"
                    checked={newAnnouncement.show_modal}
                    onChange={e => setNewAnnouncement({ ...newAnnouncement, show_modal: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="show_modal" className="text-sm">Show as popup modal</label>
                </div>

                <Button onClick={createAnnouncement} disabled={saving}>
                  <Megaphone className="h-4 w-4 mr-2" />
                  Create Announcement
                </Button>
              </CardContent>
            </Card>

            {/* Existing Announcements */}
            <Card>
              <CardHeader>
                <CardTitle>Existing Announcements</CardTitle>
              </CardHeader>
              <CardContent>
                {announcements.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No announcements yet</p>
                ) : (
                  <div className="space-y-3">
                    {announcements.map(ann => (
                      <div 
                        key={ann.id} 
                        className={`p-4 border rounded-lg ${ann.is_active ? 'border-primary/50 bg-primary/5' : 'border-border opacity-60'}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className="font-medium">{ann.title}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                ann.type === 'info' ? 'bg-blue-100 text-blue-700'
                                : ann.type === 'success' ? 'bg-green-100 text-green-700'
                                : ann.type === 'warning' ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                              }`}>
                                {ann.type}
                              </span>
                              {ann.show_modal && (
                                <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                  Modal
                                </span>
                              )}
                              {ann.delivery && ann.delivery !== 'revhapi_app' && (
                                <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                                  {ann.delivery === 'hapiguard_plugin' ? '🛡️ HapiGuard Plugin' : '📡 App + Plugin'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{ann.content?.replace(/<[^>]*>/g, '')}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Targeting: {(ann.target_tiers || []).join(', ')} • 
                              Created: {new Date(ann.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleAnnouncement(ann.id, ann.is_active)}
                              className={`p-2 rounded hover:bg-accent ${ann.is_active ? 'text-green-500' : 'text-muted-foreground'}`}
                            >
                              {ann.is_active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                            </button>
                            <button
                              onClick={() => deleteAnnouncement(ann.id)}
                              className="p-2 rounded hover:bg-red-100 text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* PLUGIN BANNERS TAB */}
        <TabsContent value="plugin-banners">
          <PluginBannersTab />
        </TabsContent>

        {/* BROADCAST NOTIFICATIONS TAB */}
        <TabsContent value="notifications">
          <div className="space-y-6">
            <Alert>
              <Bell className="h-4 w-4" />
              <AlertDescription>
                <strong>Broadcast Notifications</strong> appear in users' notification bell.
                They persist until users mark them as read.
              </AlertDescription>
            </Alert>

            {/* Send Broadcast */}
            <Card>
              <CardHeader>
                <CardTitle>Send Broadcast Notification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label="Title"
                  placeholder="e.g., System Maintenance Tonight"
                  value={broadcastForm.title}
                  onChange={e => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                />
                
                <div>
                  <label className="block text-sm font-medium mb-1">Message</label>
                  <Textarea
                    placeholder="We'll be performing maintenance from 2-4 AM UTC..."
                    value={broadcastForm.message}
                    onChange={e => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Target Tiers</label>
                  <div className="flex gap-2">
                    {['trial', 'starter', 'pro'].map(tier => (
                      <button
                        key={tier}
                        onClick={() => toggleTier(tier, broadcastForm, setBroadcastForm)}
                        className={`px-3 py-1 rounded text-sm capitalize ${
                          (broadcastForm.target_tiers || []).includes(tier)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Will send to: {
                      (broadcastForm.target_tiers.includes('trial') ? stats.trialUsers : 0) +
                      (broadcastForm.target_tiers.includes('starter') ? stats.starterUsers : 0) +
                      (broadcastForm.target_tiers.includes('pro') ? stats.proUsers : 0)
                    } users
                  </p>
                </div>

                <Button onClick={sendBroadcastNotification} disabled={saving}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Broadcast
                </Button>
              </CardContent>
            </Card>

            {/* Recent Broadcasts */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Broadcasts</CardTitle>
              </CardHeader>
              <CardContent>
                {recentNotifications.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No broadcasts sent yet</p>
                ) : (
                  <div className="space-y-3">
                    {recentNotifications.map((notif, idx) => (
                      <div key={idx} className="p-3 border border-border rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Bell className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{notif.title}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{notif.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Sent: {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ONBOARDING TAB */}
        <TabsContent value="onboarding">
          <OnboardingAdmin />
        </TabsContent>

        {/* ── DEMO LEADS ── */}
        <TabsContent value="integrations">
          <IntegrationSettings />
        </TabsContent>

        <TabsContent value="demo-leads">
          <DemoLeads />
        </TabsContent>
      </Tabs>

      {/* User Detail Modal */}
      <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)} title="User Details" size="lg">
        {selectedUser && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Username</label>
                <p className="font-medium">{selectedUser.username}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">User ID</label>
                <p className="font-mono text-xs">{selectedUser.id}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Role</label>
                <Select
                  value={selectedUser.role || 'user'}
                  onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </Select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Tier</label>
                <Select
                  value={selectedUser.tier || 'trial'}
                  onChange={(e) => setSelectedUser({ ...selectedUser, tier: e.target.value })}
                >
                  <option value="trial">Trial</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                </Select>
              </div>
            </div>

            {(selectedUser.onboarding_role || selectedUser.onboarding_platforms?.length > 0 || selectedUser.onboarding_use_cases?.length > 0) && (
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-2 text-sm">Onboarding Survey</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {selectedUser.onboarding_use_cases?.length > 0 && (
                    <div><span className="text-muted-foreground">Use cases: </span>{selectedUser.onboarding_use_cases.join(', ')}</div>
                  )}
                  {selectedUser.onboarding_role && (
                    <div><span className="text-muted-foreground">Role: </span>{ROLE_LABELS[selectedUser.onboarding_role] || selectedUser.onboarding_role}</div>
                  )}
                  {selectedUser.onboarding_platforms?.length > 0 && (
                    <div className="col-span-2"><span className="text-muted-foreground">Platforms: </span>{selectedUser.onboarding_platforms.join(', ')}</div>
                  )}
                  {selectedUser.onboarding_experience && (
                    <div><span className="text-muted-foreground">Experience: </span>{EXP_LABELS[selectedUser.onboarding_experience] || selectedUser.onboarding_experience}</div>
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 border-t">
              <h4 className="font-medium mb-2">Quick Actions</h4>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => grantTrial(selectedUser.id, 30)}>
                  +30 Days Trial
                </Button>
                <Button size="sm" variant="outline" onClick={() => grantTrial(selectedUser.id, 7)}>
                  +7 Days Trial
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateUserProfile(selectedUser.id, { tier: 'pro' })}>
                  Upgrade to Pro
                </Button>
              </div>
            </div>

            <ModalFooter>
              <Button variant="outline" onClick={() => setShowUserModal(false)}>Cancel</Button>
              <Button onClick={() => updateUserProfile(selectedUser.id, {
                role: selectedUser.role,
                tier: selectedUser.tier,
              })} loading={saving}>
                Save Changes
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SystemAdmin;

// ─────────────────────────────────────────────────────────────────────────────
// PLUGIN BANNERS TAB — standalone component, completely separate from app
// announcements. Creates rows in admin_announcements with delivery='hapiguard_plugin'
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_BANNER = {
  title:      '',
  cta_text:   '',
  cta_url:    '',
  bg_color:   '#1e40af',
  text_color: '#ffffff',
};

function PluginBannersTab() {
  const [banners, setBanners]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [form,    setForm]      = useState(EMPTY_BANNER);

  useEffect(() => { loadBanners(); }, []);

  async function loadBanners() {
    setLoading(true);
    const { data } = await supabase
      .from('admin_announcements')
      .select('id, title, cta_text, cta_url, bg_color, text_color, target_tiers, is_active, created_at')
      .eq('delivery', 'hapiguard_plugin')
      .order('created_at', { ascending: false });
    setBanners(data || []);
    setLoading(false);
  }

  async function handleSave() {
    if (!form.title.trim()) { toast.error('Message is required'); return; }
    setSaving(true);
    const payload = {
      title:        form.title.trim(),
      content:      form.title.trim(), // content mirrors title for plugin banners
      type:         'info',
      show_modal:   false,             // never shows as modal
      delivery:     'hapiguard_plugin',
      is_active:    true,
      target_tiers: [],
      cta_text:     form.cta_text.trim(),
      cta_url:      form.cta_url.trim(),
      bg_color:     form.bg_color,
      text_color:   form.text_color,
    };

    const op = form.id
      ? supabase.from('admin_announcements').update(payload).eq('id', form.id)
      : supabase.from('admin_announcements').insert(payload);

    const { error } = await op;
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(form.id ? 'Banner updated' : 'Banner created');
    setForm(EMPTY_BANNER);
    loadBanners();
  }

  async function toggleBanner(id, current) {
    await supabase.from('admin_announcements').update({ is_active: !current }).eq('id', id);
    loadBanners();
  }

  async function deleteBanner(id) {
    if (!window.confirm('Delete this plugin banner?')) return;
    await supabase.from('admin_announcements').delete().eq('id', id);
    toast.success('Banner deleted');
    loadBanners();
  }

  function editBanner(b) {
    setForm({ ...b });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <Alert>
        <span className="text-base">🛡️</span>
        <AlertDescription>
          <strong>Plugin Banners</strong> appear as dismissible banners at the top of the HapiGuard
          settings page inside WordPress. They never appear in the RevHapi dashboard.
          The plugin fetches active banners once per hour.
        </AlertDescription>
      </Alert>

      {/* Create / Edit form */}
      <Card>
        <CardHeader>
          <CardTitle>{form.id ? 'Edit Banner' : 'Create Plugin Banner'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Live preview */}
          <div className="rounded-lg overflow-hidden border border-border">
            <p className="text-[10px] text-muted-foreground px-2 py-1 bg-muted border-b border-border font-medium">
              WordPress plugin preview
            </p>
            <div className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium flex-wrap"
              style={{ background: form.bg_color, color: form.text_color }}>
              <span className="flex-1">{form.title || 'Your banner message…'}</span>
              {form.cta_text && (
                <span className="px-3 py-1 rounded-full border text-xs font-semibold"
                  style={{ borderColor: form.text_color, color: form.text_color }}>
                  {form.cta_text}
                </span>
              )}
              <span style={{ color: form.text_color, opacity: 0.6 }}>✕</span>
            </div>
          </div>

          <Input label="Message *" placeholder="e.g. HapiGuard v1.1 is out — see what's new"
            value={form.title} onChange={e => u('title', e.target.value)} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="CTA Button Text" placeholder="Learn More"
              value={form.cta_text} onChange={e => u('cta_text', e.target.value)} />
            <Input label="CTA URL" placeholder="https://revhapi.com/..."
              value={form.cta_url} onChange={e => u('cta_url', e.target.value)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Background Colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.bg_color}
                  onChange={e => u('bg_color', e.target.value)}
                  className="w-9 h-9 rounded-lg border border-border cursor-pointer p-0.5" />
                <input type="text" value={form.bg_color} maxLength={7}
                  onChange={e => u('bg_color', e.target.value)}
                  className="flex-1 px-3 py-2 border border-border rounded-lg text-sm font-mono bg-background text-foreground" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Text Colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.text_color}
                  onChange={e => u('text_color', e.target.value)}
                  className="w-9 h-9 rounded-lg border border-border cursor-pointer p-0.5" />
                <input type="text" value={form.text_color} maxLength={7}
                  onChange={e => u('text_color', e.target.value)}
                  className="flex-1 px-3 py-2 border border-border rounded-lg text-sm font-mono bg-background text-foreground" />
              </div>
            </div>
          </div>



          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving}>
              <Megaphone className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : form.id ? 'Update Banner' : 'Create Banner'}
            </Button>
            {form.id && (
              <Button variant="outline" onClick={() => setForm(EMPTY_BANNER)}>Cancel</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Banner list */}
      <Card>
        <CardHeader>
          <CardTitle>Active Plugin Banners ({banners.filter(b => b.is_active).length} live)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-center py-8">Loading…</p>
          ) : banners.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No plugin banners yet</p>
          ) : (
            <div className="space-y-3">
              {banners.map(b => (
                <div key={b.id}
                  className={`border rounded-xl overflow-hidden ${b.is_active ? 'border-primary/40' : 'border-border opacity-60'}`}>
                  <div className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium flex-wrap"
                    style={{ background: b.bg_color, color: b.text_color }}>
                    <span className="flex-1">{b.title}</span>
                    {b.cta_text && (
                      <span className="px-2 py-0.5 rounded-full border text-xs"
                        style={{ borderColor: b.text_color, color: b.text_color }}>{b.cta_text}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-muted/20 border-t border-border flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      b.is_active ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                  : 'bg-muted text-muted-foreground'
                    }`}>
                      {b.is_active ? 'Live' : 'Paused'}
                    </span>

                    <span className="text-xs text-muted-foreground ml-auto">
                      {new Date(b.created_at).toLocaleDateString()}
                    </span>
                    <button onClick={() => editBanner(b)}
                      className="text-xs px-2 py-1 rounded border border-border hover:bg-muted transition-colors text-foreground">
                      Edit
                    </button>
                    <button onClick={() => toggleBanner(b.id, b.is_active)}
                      className="text-xs px-2 py-1 rounded border border-border hover:bg-muted transition-colors text-foreground">
                      {b.is_active ? 'Pause' : 'Activate'}
                    </button>
                    <button onClick={() => deleteBanner(b.id)}
                      className="text-xs px-2 py-1 rounded border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-500 transition-colors">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
